import Coordinate from "../shapes/support/Coordinate";
import ImageCoordinate from "../shapes/support/ImageCoordinate";
import ImageHorizontalBorder from "../shapes/support/ImageHorizontalBorder";
import ImageVerticalBorder from "../shapes/support/ImageVerticalBorder";
import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import ImageTool from "../tools/edit/ImageTool";

export default class Image extends AbstractProvidedClass {
    _loaded = false;
    srcImg = null;

    constructor(provider, data, justCreated = false) {
        super(provider);

        this.id = data.id;
        this.uri = data.url || data.thumb_url;
        this.rotation = data.rotation || 0;
        this.absX = data.x || 100;
        this.absY = data.y || 100;
        this.opacity = data.opacity || 255;
        this.srcImg = this.p.loadImage(
            this.uri,
            (img) => this.loadImage(img, data.width, data.height, justCreated)
        );
    }

    loadImage(img, displayWidth, displayHeight, justCreated) {
        this.width = img.width;
        this.height = img.height;
        this.initCorners(displayWidth, displayHeight);
        this.initBorders();

        this.scaleImage();
        if (justCreated) {
            this.changesMade();
        }
        this.imageMenu?.loadingFinished();
        this._loaded = true;
    }

    display() {
        if (!this._loaded || !this.currentImg || this.currentWidth < 1 || this.currentHeight < 1) {
            return;
        }

        this.p.push();
        this.p.translate(this.currentX, this.currentY);
        this.p.rotate(this.rotation);

        if (this.currentWidth != this.currentImg.width
            || this.currentHeight != this.currentImg.height
        ) {
            this.scaleImage();
        }

        this.p.imageMode(this.p.CENTER);
        this.p.image(this.currentImg, 0, 0);
        let color = this.p.color(this.backgroundColor.toString());
        color.setAlpha(255 - this.opacity);
        this.p.fill(color);
        this.p.noStroke(0);
        this.p.rectMode(this.p.CORNERS);
        this.p.rect(
            this.corners[0].currentX - 1, this.corners[0].currentY - 1,
            this.corners[1].currentX + 1, this.corners[2].currentY + 1
        );
        let tool = this.tools.currentTool;
        if (tool instanceof ImageTool && tool.mode == 'move') {
            this.corners.map(c => c.display());
        }
        this.p.pop();
    }

    hovered(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return -this.currentWidth / 2 <= viewX
            && this.currentWidth / 2 >= viewX
            && -this.currentHeight / 2 <= viewY
            && this.currentHeight / 2 >= viewY;
    }

    hoveredCorners(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return this.corners.find((c) => c.hovered(viewX, viewY));
    }

    hoveredWithSupport(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return this.hoveredCorners(viewX, viewY)
            || (this.hovered(viewX, viewY) ? this : null);
    }

    mouseOver(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        if (this.corners.find(c => c.mouseOver(viewX, viewY))) {
            return true;
        }

        if (this.hovered()) {
            this.cursor.grab();
            return true;
        }
        this.cursor.arrow();
        return false;
    }

    move(dx, dy) {
        this.absX += dx;
        this.absY += dy;
    }

    scale(scale) {
        this.corners.map(c => {
            c.absX *= scale;
            c.absY *= scale;
        })
        this.scaleImage();
    }

    scaleImage() {
        this.currentImg = this.p.createImage(this.currentWidth, this.currentHeight);
        this.currentImg.copy(
            this.srcImg,
            0, 0,
            this.width, this.height,
            0, 0,
            this.currentWidth, this.currentHeight
        );
    }

    initCorners(displayWidth, displayHeight) {
        let width = displayWidth || this.width;
        let height = displayHeight || this.height;
        this.corners = [
            new ImageCoordinate(this.provider, -width / 2, -height / 2, this), // top left
            new ImageCoordinate(this.provider, width / 2, height / 2, this), // bottom right
            new ImageCoordinate(this.provider, -width / 2, height / 2, this), // bottom left
            new ImageCoordinate(this.provider, width / 2, -height / 2, this), // top right
        ];
    }

    initBorders() {
        this.borders = [
            new ImageVerticalBorder(this.provider, this, this.corners[0], this.corners[2]), // left
            new ImageVerticalBorder(this.provider, this, this.corners[1], this.corners[3]), // right
            new ImageHorizontalBorder(this.provider, this, this.corners[0], this.corners[3]), // top
            new ImageHorizontalBorder(this.provider, this, this.corners[1], this.corners[2]), // bottom
        ];

        this.borders[0].setOpposite(this.borders[1]);
        this.borders[1].setOpposite(this.borders[0]);
        this.borders[2].setOpposite(this.borders[3]);
        this.borders[3].setOpposite(this.borders[2]);

        this.corners[0].setRelations(this.borders[2], this.borders[0]);
        this.corners[1].setRelations(this.borders[3], this.borders[1]);
        this.corners[2].setRelations(this.borders[3], this.borders[0]);
        this.corners[3].setRelations(this.borders[2], this.borders[1]);
    }

    updateCorners(width, height) {
        let halfWidth = width ? width / 2 : this.displayWidth / 2;
        let halfHeight = height ? height / 2 : this.displayHeight / 2;

        // We have to first retrieve the corners, before changing
        // the values, because changing the values will change which
        // corners are returned.
        let rightCorners = this.rightCorners;
        let leftCorners = this.leftCorners;
        let bottomCorners = this.bottomCorners;
        let topCorners = this.topCorners;

        rightCorners.map(c => c.absX = halfWidth);
        leftCorners.map(c => c.absX = -halfWidth);
        bottomCorners.map(c => c.absY = halfHeight);
        topCorners.map(c => c.absY = -halfHeight);

        this.scaleImage();
    }

    ensureViewXandY(viewX, viewY) {
        if (viewX === undefined || viewY === undefined) {
            return this.view.getRelative(this.currentX, this.currentY, this.rotation);
        }
        return [viewX, viewY];
    }

    get currentX() {
        return this.absX * this.view.scale;
    }

    get currentY() {
        return this.absY * this.view.scale;
    }

    get currentWidth() {
        return this.displayWidth * this.view.scale;
    }

    get currentHeight() {
        return this.displayHeight * this.view.scale;
    }

    get ratio() {
        return this.width / this.height;
    }

    get displayWidth() {
        return Math.abs(this.corners[0].absX - this.corners[1].absX);
    }

    get displayHeight() {
        return Math.abs(this.corners[0].absY - this.corners[2].absY);
    }

    get rightCorners() {
        return this.corners[1].absX > this.corners[0].absX
            ? [this.corners[1], this.corners[3]]
            : [this.corners[0], this.corners[2]];
    }

    get leftCorners() {
        return this.corners[1].absX < this.corners[0].absX
            ? [this.corners[1], this.corners[3]]
            : [this.corners[0], this.corners[2]];
    }

    get bottomCorners() {
        return this.corners[1].absY > this.corners[0].absY
            ? [this.corners[1], this.corners[2]]
            : [this.corners[0], this.corners[3]];
    }

    get topCorners() {
        return this.corners[1].absY < this.corners[0].absY
            ? [this.corners[1], this.corners[2]]
            : [this.corners[0], this.corners[3]];
    }

    get center() {
        return new Coordinate(
            this.provider,
            this.absX,
            this.absY
        );
    }

    export() {
        return {
            id: this.id,
            url: this.uri,
            rotation: this.rotation,
            x: this.absX,
            y: this.absY,
            width: this.displayWidth,
            height: this.displayHeight,
            opacity: this.opacity,
        };
    }

    // This is a workaround for if for some reason we need headers.
    // loadImage(displayWidth, displayHeight, justCreated) {
    //     fetch(this.uri, {
    //         method: 'GET',
    //         headers: {
    //             'X-CSRF-Token': this.p.select('meta[name="csrf-token"]').elt.content,
    //             'Access-Control-Allow-Origin': '*'
    //         }
    //     })
    //         .then(response => response.blob())
    //         .then(blob => {
    //             createImageBitmap(blob)
    //                 .then(bitmap => {
    //                     this.srcImg = new p5.Image(bitmap.width, bitmap.height);
    //                     this.srcImg.drawingContext.drawImage(bitmap, 0, 0);
    //                     this.srcImg.modified = true;

    //                     this.loadImage(this.srcImg, displayWidth, displayHeight, justCreated);
    //                 })
    //         });
    // }
}
